import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DeviceService,
  NMultiselectComponent,
} from '@nomoh-monorepo/shared-common-ui';
import { IPermission } from '@nomoh-monorepo/shared-types';

// PrimeNG imports
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DividerModule } from 'primeng/divider';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

// Data access
import {
  PermissionsDataService,
  PermissionsService,
} from '@nomoh-monorepo/data-access-nomoh-administrador';

// Utils
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';

/**
 * Interface para permissão com informações de disponibilidade
 */
interface IPermissionWithPrice extends IPermission {
  priceMonthly?: number | null;
  priceYearly?: number | null;
  availabilityType?: 'plan' | 'standalone';
  availabilityId?: string; // ID da tabela de availability (necessário para remover)
}

@Component({
  selector: 'lib-pessoa-permissions-extras-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    TagModule,
    ProgressSpinnerModule,
    TooltipModule,
    ConfirmDialogModule,
    TranslateModule,
    PaginatorModule,
    MultiSelectModule,
    DividerModule,
    NMultiselectComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './pessoa-permissions-extras-tab.component.html',
  styleUrls: ['./pessoa-permissions-extras-tab.component.scss'],
})
export class PessoaPermissionsExtrasTabComponent implements OnInit {
  @Input() userId!: string;
  @Input() canEdit = true; // ✅ Novo: controla se pode editar permissões

  private permissionsService = inject(PermissionsService);
  private permissionsDataService = inject(PermissionsDataService);
  private notificacaoService = inject(NotificacaoService);
  private confirmationService = inject(ConfirmationService);
  private translate = inject(TranslateService);
  readonly deviceService = inject(DeviceService);

  // Signals readonly para template
  readonly $loading = this.permissionsDataService.$loading;

  // Signals locais
  planPermissions = signal<IPermissionWithPrice[]>([]);
  extraPermissions = signal<IPermissionWithPrice[]>([]);
  availableStandalonePermissions = signal<IPermissionWithPrice[]>([]);
  selectedPermissionsToAdd = signal<IPermissionWithPrice[]>([]);

  // Paginação - Permissões Herdadas
  currentPagePlan = signal(0);
  rowsPerPagePlan = signal(6);

  // Paginação - Permissões Extras
  currentPageExtra = signal(0);
  rowsPerPageExtra = signal(6);

  // Computed: permissões paginadas - Herdadas
  paginatedPlanPermissions = computed(() => {
    const page = this.currentPagePlan();
    const rows = this.rowsPerPagePlan();
    const start = page * rows;
    const end = start + rows;
    return this.planPermissions().slice(start, end);
  });

  // Computed: permissões paginadas - Extras
  paginatedExtraPermissions = computed(() => {
    const page = this.currentPageExtra();
    const rows = this.rowsPerPageExtra();
    const start = page * rows;
    const end = start + rows;
    return this.extraPermissions().slice(start, end);
  });

  ngOnInit(): void {
    if (this.userId) {
      this.carregarPermissoes();
    }
  }

  async carregarPermissoes(): Promise<void> {
    try {
      // Buscar permissões do usuário (herdadas + extras)
      const result = await this.permissionsService.buscarPermissoesUsuario(
        this.userId
      );

      // ✅ Garantir que sempre sejam arrays válidos
      if (result) {
        this.planPermissions.set(
          (result.fromPlan as IPermissionWithPrice[]) || []
        );
        this.extraPermissions.set(
          (result.extra as IPermissionWithPrice[]) || []
        );
      } else {
        this.planPermissions.set([]);
        this.extraPermissions.set([]);
      }

      // Buscar permissões disponíveis (apenas standalone) se pode editar
      if (this.canEdit) {
        await this.carregarPermissoesDisponiveis();
      }
    } catch (error) {
      this.notificacaoService.notificacaoError(
        this.translate.instant('USER_PERMISSIONS_EXTRAS.NOTIFICACAO_ERRO')
      );
      console.error(error);
    }
  }

  async carregarPermissoesDisponiveis(): Promise<void> {
    try {
      // ✅ Carregar apenas permissões standalone
      await this.permissionsDataService.carregarPermissions({
        offSet: 0,
        limit: 1000,
        type: 'standalone', // ✅ Filtro para buscar apenas standalone
      });

      const allPermissions =
        (this.permissionsDataService.$permissions() as IPermissionWithPrice[]) ||
        [];

      // ✅ Garantir que os arrays sejam válidos antes de usar .map()
      const planPerms = this.planPermissions() || [];
      const extraPerms = this.extraPermissions() || [];

      // Filtrar permissões que o usuário ainda não tem
      const currentPermissionIds = [
        ...planPerms.map((p) => p.id),
        ...extraPerms.map((p) => p.id),
      ];

      const available = allPermissions.filter(
        (p) => !currentPermissionIds.includes(p.id)
      );

      this.availableStandalonePermissions.set(available);
    } catch (error) {
      console.error('Erro ao carregar permissões disponíveis:', error);
    }
  }

  confirmarRemoverExtra(permission: IPermissionWithPrice): void {
    if (!this.canEdit) return;

    this.confirmationService.confirm({
      message: this.translate.instant(
        'USER_PERMISSIONS_EXTRAS.VALIDACAO_ERRO_CONFLITO_EXTRA',
        { key: permission.key }
      ),
      header: this.translate.instant('USER_PERMISSIONS_EXTRAS.BTN_REMOVER'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant(
        'FEATURE_PERMISSIONS.CONFIRMACAO_ACEITAR'
      ),
      rejectLabel: this.translate.instant(
        'FEATURE_PERMISSIONS.CONFIRMACAO_REJEITAR'
      ),
      accept: async () => {
        await this.removerPermissaoExtra(permission);
      },
    });
  }

  async removerPermissaoExtra(permission: IPermissionWithPrice): Promise<void> {
    if (!this.canEdit) return;

    try {
      // ✅ Usar availabilityId ao invés de permission.id
      if (!permission.availabilityId) {
        throw new Error('Permissão sem availabilityId');
      }

      const sucesso = await this.permissionsService.removerPermissoesExtras(
        this.userId,
        [permission.availabilityId]
      );

      if (sucesso) {
        this.notificacaoService.notificacaoSuccess(
          this.translate.instant('USER_PERMISSIONS_EXTRAS.NOTIFICACAO_SUCESSO')
        );
        await this.carregarPermissoes();
      } else {
        this.notificacaoService.notificacaoError(
          this.translate.instant('USER_PERMISSIONS_EXTRAS.NOTIFICACAO_ERRO')
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : this.translate.instant('USER_PERMISSIONS_EXTRAS.NOTIFICACAO_ERRO');
      this.notificacaoService.notificacaoError(errorMessage);
      console.error(error);
    }
  }

  async adicionarExtras(): Promise<void> {
    if (!this.canEdit) return;

    const permissionsToAdd = this.selectedPermissionsToAdd();

    if (permissionsToAdd.length === 0) {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant(
          'FEATURE_PERMISSIONS.TAB2_AVISO_SELECIONE_PERMISSOES'
        )
      );
      return;
    }

    // Validação Frontend: Verificar se permissões já existem nos planos
    const planPermissionKeys = this.planPermissions().map((p) => p.key);
    const conflitos = permissionsToAdd.filter((p) =>
      planPermissionKeys.includes(p.key)
    );

    if (conflitos.length > 0) {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant(
          'USER_PERMISSIONS_EXTRAS.VALIDACAO_ERRO_CONFLITO_PLANO',
          { key: conflitos.map((c) => c.key).join(', ') }
        )
      );
      return;
    }

    try {
      // ✅ CORREÇÃO: Usar availabilityId ao invés de id
      const availabilityIds = permissionsToAdd
        .map((p) => p.availabilityId)
        .filter((id): id is string => id !== undefined && id !== null);

      // ✅ Validar que todos têm availabilityId
      if (availabilityIds.length !== permissionsToAdd.length) {
        this.notificacaoService.notificacaoWarn(
          'Algumas permissões não possuem availabilityId. Recarregue a página.'
        );
        return;
      }

      const sucesso = await this.permissionsService.adicionarPermissoesExtras(
        this.userId,
        availabilityIds
      );

      if (sucesso) {
        this.notificacaoService.notificacaoSuccess(
          this.translate.instant('USER_PERMISSIONS_EXTRAS.NOTIFICACAO_SUCESSO')
        );
        this.selectedPermissionsToAdd.set([]);
        await this.carregarPermissoes();
      } else {
        this.notificacaoService.notificacaoError(
          this.translate.instant('USER_PERMISSIONS_EXTRAS.NOTIFICACAO_ERRO')
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : this.translate.instant('USER_PERMISSIONS_EXTRAS.NOTIFICACAO_ERRO');
      this.notificacaoService.notificacaoError(errorMessage);
      console.error(error);
    }
  }

  formatarPreco(preco: number | null | undefined): string {
    if (preco === null || preco === undefined) {
      return '-';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(preco);
  }

  onPageChangePlan(event: { page?: number; rows?: number }): void {
    if (event.page !== undefined) {
      this.currentPagePlan.set(event.page);
    }
    if (event.rows !== undefined) {
      this.rowsPerPagePlan.set(event.rows);
    }
  }

  onPageChangeExtra(event: { page?: number; rows?: number }): void {
    if (event.page !== undefined) {
      this.currentPageExtra.set(event.page);
    }
    if (event.rows !== undefined) {
      this.rowsPerPageExtra.set(event.rows);
    }
  }
}
